import { inject } from '@angular/core';
import { CanMatchFn } from '@angular/router';
import { ContentBundleFacade } from '@cigna/shared/angular/resource-content-data-access';
import {
  CfbFeatureFlagService,
  ContentBundleName,
} from '@cigna/cigna-for-brokers/shared/util';
import { asapScheduler } from 'rxjs';
import { map, observeOn } from 'rxjs/operators';

/**
 * Same gate as `cfbIntegrationOnCanMatch`, but safe to use on routes that can be
 * entered directly.
 *
 * The integration flag is read from the content bundle, and every router guard runs
 * before any resolver, so on a refresh or a deep link the bundle the route relies on
 * has not been fetched yet and the flag would always read as disabled. Requesting the
 * bundle here first keeps the gate correct however the route is reached; `loadBundle`
 * resolves straight away once the bundle is already in the store, so in-app
 * navigation is unaffected.
 */
export const cfbIntegrationOnContentReadyCanMatch: CanMatchFn = () => {
  /* Captured up front: the pipe callback runs outside the injection context. */
  const cfbFeatureFlagService = inject(CfbFeatureFlagService);

  return inject(ContentBundleFacade)
    .loadBundle([ContentBundleName.CIGNAFORBROKERS])
    .pipe(
      /**
       * `loadBundle` completes off the store, but the flag is read from
       * ngx-translate, and NgRx notifies selectors before it runs effects. The
       * bundle therefore reads as loaded one tick before the effect that copies it
       * into ngx-translate, which would leave the flag looking disabled. Waiting a
       * microtask lets that effect run first.
       */
      observeOn(asapScheduler),
      map(() => cfbFeatureFlagService.isIntegrationOn()),
    );
};
